import 'whatwg-fetch';
import { PasswordAuthenticator } from './auth';
import { isMobileApp } from 'utils';
import { detectWebGLKind } from './detect-webgl';
import branding from 'shared/app/model/branding';
// See the source of InflateUI API method for the list of api_levels
export const api_level = 2;
const client_version = UI_VERSION || 'dev';
export default class API {
    userInfo;
    default_authenticator;
    options;
    constructor(userInfo, default_authenticator, options) {
        this.userInfo = userInfo;
        this.default_authenticator = default_authenticator;
        this.options = options;
    }
    modify(userInfo, default_authenticator, options) {
        return new API(userInfo ?? this.userInfo, default_authenticator ?? this.default_authenticator, options ?? this.options);
    }
    prepareRequestParams(callOptions) {
        const authenticator = callOptions.authenticator ?? this.default_authenticator;
        const headers = {
            'X-ZONT-Client': isMobileApp() ? 'app' : 'web',
            'X-ZONT-Client-Version': client_version,
            'X-ZONT-Brand': branding.code,
            'X-ZONT-WebGL-Support': detectWebGLKind(),
            ...(this.userInfo?.as_headers() ?? {}),
            ...(authenticator?.post_headers() ?? {}),
            ...(callOptions.additionalHeaders ?? {}),
        };
        let body;
        if (window.FormData != null && callOptions.data instanceof window.FormData)
            body = callOptions.data;
        else {
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify(callOptions.data);
        }
        return {
            method: 'POST',
            mode: 'cors',
            cache: 'no-cache',
            credentials: authenticator?.corsCredentials() ?? 'same-origin',
            body,
            headers,
            signal: callOptions.abortSignal,
        };
    }
    prepareGetArgs(callOptions, require_authentication) {
        const authenticator = require_authentication
            ? callOptions.authenticator ?? this.default_authenticator
            : undefined;
        const args = {
            _client: isMobileApp() ? 'app' : 'web',
            _client_version: client_version,
            _brand: branding.code,
            ...(callOptions.data ?? {}),
            ...(authenticator?.get_args() ?? {}),
            ...(this.userInfo?.as_args() ?? {}),
        };
        return Object.entries(args)
            .map(([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`)
            .join('&');
    }
    get_url(method, data, require_authentication) {
        return `${this.get_baseurl()}${method}?${this.prepareGetArgs({ data }, require_authentication)}`;
    }
    get_all_news() {
        return this.call_v2({ method: 'get_all_news' });
    }
    get_boiler_models() {
        return this.call_v2({
            method: 'get_boiler_models',
        });
    }
    get_boiler_models_v2() {
        return this.call_v2({
            method: 'get_boiler_models_v2',
        });
    }
    billing_info() {
        return this.call_v2({ method: 'billing_info' });
    }
    top_up_auto_invoice(request) {
        return this.call_v2({
            method: 'top_up',
            data: { payment_method: 'auto_invoice', ...request },
        });
    }
    top_up_yakassa(request) {
        return this.call_v2({
            method: 'top_up',
            data: { payment_method: 'yakassa', ...request },
        });
    }
    verify_receipt_ios(transaction, product_id) {
        return this.call_v2({
            method: 'verify_receipt_ios',
            data: {
                transaction,
                product_id,
            },
        });
    }
    verified_purchase(vp, product_id) {
        return this.call_v2({
            method: 'verified_purchase',
            data: {
                vp,
                product_id,
            },
        });
    }
    verified_purchase2(transaction, product_id) {
        return this.call_v2({
            method: 'verified_purchase',
            data: {
                transaction,
                product_id,
            },
        });
    }
    pay(pay_intent) {
        return this.call_v2({
            method: 'pay',
            data: { pay_intent },
        });
    }
    buy_history(months) {
        return this.call_v2({
            method: 'buy_history',
            data: { months },
        });
    }
    get_account() {
        return this.call_v2({
            method: 'get_account',
            data: {},
        });
    }
    sx_download_config(device_id) {
        return this.call_v2({
            method: 'sx_download_config',
            data: { device_id },
        });
    }
    sx_upload_config(device_id, config_file) {
        const data = new FormData();
        data.append('device_id', device_id.toString());
        data.append('config_file', config_file);
        return this.call_v2({
            method: 'sx_upload_config',
            data,
        });
    }
    load_thermometers(device_id) {
        return this.call_v2({
            method: 'load_thermometers',
            data: { device_id },
        });
    }
    buy_h2000plus_firmware(serial) {
        return this.call_v2({
            method: 'buy_h2000plus_firmware',
            data: { serial },
        });
    }
    disable_auto_payment() {
        return this.call_v2({
            method: 'disable_auto_payment',
        });
    }
    enable_auto_payment() {
        return this.call_v2({
            method: 'enable_auto_payment',
        });
    }
    update_date_last_reading() {
        return this.call_v2({ method: 'update_date_last_reading' });
    }
    register_sim(msisdn, iccid) {
        return this.call_v2({
            method: 'sim/register_sim',
            data: { msisdn, iccid },
        });
    }
    load_data(data) {
        return this.call_v2({
            method: 'load_data',
            data: {
                requests: data,
            },
        });
    }
    events_loc(device_id, mintime, maxtime, limit, except) {
        return this.call_v2({
            method: 'events_loc',
            data: {
                device_id,
                mintime,
                maxtime,
                limit,
                except,
            },
        });
    }
    important_events_loc() {
        return this.call_v2({
            method: 'important_events_loc',
        });
    }
    reverse_geocode(points, request_from = '') {
        let data;
        if (request_from !== '')
            data = { points, request_from };
        else
            data = { points };
        return this.call_v2({
            method: 'reverse_geocode',
            data,
        });
    }
    opentherm_error_description(boiler_type, oem_errcode) {
        return this.call_v2({
            method: 'opentherm_error_description',
            data: { boiler_type, oem_errcode },
        });
    }
    opentherm_error_description_v2(boiler_type, ba_protocol, ot_state) {
        return this.call_v2({
            method: 'opentherm_error_description_v2',
            data: { boiler_type, ot_state, ba_protocol },
        });
    }
    send_text_to_device(device_id, text, session_id, request_id) {
        return this.call_v2({
            method: 'send_text_to_device',
            data: { device_id, text, session_id, request_id },
        });
    }
    request_config_file(device_id, service_password) {
        return this.call_v2({
            method: 'request_config_file',
            data: {
                device_id,
                // undefined will remove this parameter from JSON
                service_password: service_password ?? undefined,
            },
        });
    }
    upload_config_file(device_id, service_password, config_file) {
        const data = new FormData();
        data.append('device_id', device_id.toString());
        data.append('service_password', service_password);
        data.append('config_file', config_file);
        return this.call_v2({ method: 'upload_config_file', data });
    }
    send_z3k_command(device_id, command, is_guaranteed, request_time) {
        return this.call_v2({
            method: 'send_z3k_command',
            data: {
                device_id,
                ...command,
                request_time: request_time,
                is_guaranteed,
            },
        });
    }
    save_z3k_config(device_id, config, commands, reason) {
        return this.call_v2({
            method: 'save_z3k_config',
            data: { device_id, config, commands, reason },
        });
    }
    upload_z3k_config_from_file(data) {
        return this.call_v2({
            method: 'upload_z3k_config_from_file',
            data: data,
        });
    }
    get_stock_configs(device_type) {
        return this.call_v2({
            method: 'get_stock_configs',
            data: { device_type },
        });
    }
    apply_stock_config(device_id, config_id) {
        return this.call_v2({
            method: 'apply_stock_config',
            data: { device_id, config_id },
        });
    }
    replace_device(broken_device_id, replacement_device_id) {
        return this.call_v2({
            method: 'replace_device',
            data: { broken_device_id, replacement_device_id },
        });
    }
    update_device(device_id, device_settings) {
        return this.call_v2({
            method: 'update_device',
            data: { device_id, ...device_settings },
        });
    }
    requestDeviceIO(device_id, portname, type, value) {
        return this.call_v2({
            method: 'set_io_port',
            data: { device_id, portname, type, value },
        });
    }
    sendCustomCommand(device_id, command_id) {
        return this.call_v2({
            method: 'send_custom_command',
            data: { device_id, command_id },
        });
    }
    format_file_system(device_id) {
        return this.call_v2({
            method: 'format_filesystem',
            data: { device_id },
        });
    }
    delete_device(device_id, clear_data) {
        return this.call_v2({
            method: 'delete_device',
            data: {
                device_id,
                clear_data,
            },
        });
    }
    transfer_device(transfer_device_id, new_user_login, clear_access, clear_data) {
        return this.call_v2({
            method: 'delete_device',
            data: {
                device_id: transfer_device_id,
                transfer: true,
                transfer_username: new_user_login,
                clear_access,
                clear_data,
            },
        });
    }
    inflate_ui(api_level, load_io) {
        return this.call_v2({
            method: 'inflate_ui',
            data: { api_level, load_io },
        });
    }
    test_auth() {
        return this.call_v2({ method: 'test_auth' });
    }
    update_device_tree(device_tree, device_tree_update_time) {
        return this.call_v2({
            method: 'update_device_tree',
            data: {
                device_tree: device_tree,
                device_tree_update_time: device_tree_update_time,
            },
        });
    }
    create_geozone(geozone) {
        return this.call_v2({
            method: 'create_geozone',
            data: {
                geozone,
            },
        });
    }
    update_geozones(geozones) {
        return this.call_v2({
            method: 'update_geozones',
            data: {
                geozones,
            },
        });
    }
    delete_geozones(geozones) {
        return this.call_v2({
            method: 'delete_geozones',
            data: {
                geozones,
            },
        });
    }
    upload_geozones(files) {
        const data = new FormData();
        for (let i = 0; i < files.length; i++) {
            data.append(`file-${i}`, files[i]);
        }
        return this.call_v2({
            method: 'upload_geozones_from_file',
            data,
        });
    }
    add_history_maintenance(device_id, maintenance) {
        return this.call_v2({
            method: 'add_history_maintenance',
            data: {
                device_id,
                maintenance,
            },
        });
    }
    delete_history_maintenance(device_id, maintenance_id) {
        return this.call_v2({
            method: 'delete_history_maintenance',
            data: {
                device_id,
                maintenance_id,
            },
        });
    }
    edit_history_maintenance(device_id, maintenance) {
        return this.call_v2({
            method: 'edit_history_maintenance',
            data: {
                device_id,
                maintenance,
            },
        });
    }
    upload_maintenance_attachment(device_id, attachments) {
        const data = new FormData();
        data.append('device_id', device_id.toString());
        for (let i = 0; i < attachments.length; i++)
            data.append(`file-${i}`, attachments[i].file);
        return this.call_v2({
            method: 'upload_maintenance_attachment',
            data,
        });
    }
    uploading_icons_user(packIcons) {
        const data = new FormData();
        for (let i = 0; i < packIcons.length; i++) {
            data.append(`file-${i}`, packIcons[i].file);
            data.append(`maintainColor-${i}`, packIcons[i].maintainColor.toString());
        }
        return this.call_v2({
            method: 'uploading_icons_user',
            data,
        });
    }
    delete_icons_user(icons_id) {
        return this.call_v2({
            method: 'delete_icons_user',
            data: {
                icons_id,
            },
        });
    }
    download_maintenance_attachment(device_id, attachment_id) {
        return this.call_v2({
            method: 'download_maintenance_attachment',
            data: {
                device_id,
                attachment_id,
            },
        });
    }
    get_photos(device_id, time_from) {
        return this.call_v2({
            method: 'get_photos_unixtimes',
            data: {
                device_id,
                min_unixtime: time_from,
            },
        });
    }
    get_photos_by_period(device_id, time) {
        return this.call_v2({
            method: 'get_photos_by_period',
            data: {
                device_id,
                unixtimes: time,
            },
        });
    }
    update_account(updater, username, current_password) {
        return this.call_v2({
            method: 'update_account',
            data: updater,
            authenticator: new PasswordAuthenticator(username, current_password),
        });
    }
    delete_account(username, current_password) {
        return this.call_v2({
            method: 'delete_account',
            authenticator: new PasswordAuthenticator(username, current_password),
        });
    }
    set_ui_settings(setter) {
        return this.call_v2({ method: 'set_ui_settings', data: setter });
    }
    get_sim_info(iccid_or_msisdn) {
        return this.call_v2({
            method: 'sim/get_sim_info',
            data: { iccid_or_msisdn },
        });
    }
    asp_billing_delete_client(client_user_id) {
        return this.call_v2({
            method: 'asp_billing/delete_client',
            data: { client_user_id },
        });
    }
    add_device(dont_mark_as_dirty, fields, only_check = false) {
        return this.call_v2({
            method: 'add_device',
            data: {
                dont_mark_as_dirty,
                only_check,
                ...fields,
            },
        });
    }
    sim_remove(sim_type, msisdn, iccid) {
        return this.call_v2({
            method: 'sim/sim_remove',
            data: { msisdn, sim_type, iccid },
        });
    }
    get_prolong_info(sim_id, months, timezone) {
        return this.call_v2({
            method: 'sim/get_prolong_info',
            data: { sim_id, months, timezone },
        });
    }
    prolong_sim(sim_id, months, timezone) {
        return this.call_v2({
            method: 'sim/prolong',
            data: { sim_id, months, timezone },
        });
    }
    set_sim_auto_prolong(sim_id, auto_prolong) {
        return this.call_v2({
            method: 'sim/set_auto_prolong',
            data: { sim_id, auto_prolong },
        });
    }
    request_radio_silence(device_id, is_radio_silence) {
        return this.call_v2({
            method: 'request_radiosilence',
            data: { device_id, state: is_radio_silence },
        });
    }
    get_firmware_releases(device_id) {
        return this.call_v2({
            method: 'get_firmware_releases',
            data: { device_id },
        });
    }
    upgrade_firmware(device_id, firmware_release_id, slow_mode) {
        return this.call_v2({
            method: 'upgrade_firmware',
            data: { device_id, firmware_release_id, slow_mode },
        });
    }
    upgrade_firmware_custom(device_id, dont_reboot, files, slow_mode) {
        const data = new FormData();
        data.append('device_id', device_id.toString());
        data.append('dont_reboot', dont_reboot.toString());
        data.append('slow_mode', slow_mode.toString());
        for (const customFile of files) {
            data.append(customFile.code, customFile.file);
            data.append(`${customFile.code}_remote_filename`, customFile.filename);
        }
        return this.call_v2({
            method: 'upgrade_firmware_custom',
            data,
        });
    }
    upload_files(device_id, dont_reboot, prefix, files, slow_mode) {
        const data = new FormData();
        data.append('device_id', device_id.toString());
        data.append('prefix', prefix);
        data.append('dont_reboot', dont_reboot.toString());
        data.append('slow_mode', slow_mode.toString());
        for (let i = 0; i < files.length; i++)
            data.append(`file-${i}`, files[i]);
        return this.call_v2({
            method: 'upload_files',
            data,
        });
    }
    check_username(username) {
        return this.call_v2({
            method: 'check_username',
            data: { username },
        });
    }
    check_email(email) {
        return this.call_v2({
            method: 'check_email',
            data: { email },
        });
    }
    js_exception(errors, errorInfo) {
        return this.call_v2({
            method: 'js_exception',
            data: {
                errors,
                errorInfo,
            },
        });
    }
    downgrade_tariff(target_tariff) {
        return this.call_v2({
            method: 'downgrade_tariff',
            data: { target_tariff },
        });
    }
    cancel_tariff_downgrade() {
        return this.call_v2({
            method: 'cancel_tariff_downgrade',
        });
    }
    upgrade_tariff(target_tariff) {
        return this.call_v2({
            method: 'upgrade_tariff',
            data: { target_tariff },
        });
    }
    get_tariffs() {
        return this.call_v2({ method: 'get_tariffs' });
    }
    generate_archive(device_id, mintime, maxtime, split_by) {
        return this.call_v2({
            method: 'generate_archive',
            data: { device_id, mintime, maxtime, split_by },
        });
    }
    get_archive_photos(device_id, unixtimes) {
        return this.call_to_get_blob({
            method: 'download_archive_photos',
            data: { device_id, unixtimes },
        });
    }
    create_issue_from_support(user) {
        if (user.files != null) {
            const data = new FormData();
            const { files, ...rest } = user;
            Object.entries(rest).forEach(([key, value]) => value != null && data.append(key, value.toString()));
            files.forEach((file, i) => data.append(`file-${i}`, file));
            return this.call_v2({
                method: 'support/create_issue',
                data,
            });
        }
        return this.call_v2({
            method: 'support/create_issue',
            data: user,
        });
    }
    get_user_issues() {
        return this.call_v2({
            method: 'support/get_user_issues',
            data: {},
        });
    }
    get_issue(ticket_id) {
        return this.call_v2({
            method: 'support/get_issue',
            data: { ticket_id },
        });
    }
    send_comment(ticket_id, comment) {
        return this.call_v2({
            method: 'support/send_comment',
            data: { ticket_id, comment },
        });
    }
    convert_html_report_to_xlsx(html) {
        return this.call_to_get_blob({
            method: 'autoscan/html_report_to_xlsx',
            data: { html },
        });
    }
    format_ztc_700(device_id) {
        return this.call_v2({
            method: 'format_ztc700',
            data: { device_id },
        });
    }
    fix_hw_205_amr(device_id) {
        return this.call_v2({
            method: 'fix_hw205_amr',
            data: { device_id },
        });
    }
    downloadL1000Log(device_id) {
        return this.call_v2({
            method: 'request_file_download',
            data: {
                device_id,
                filename: 'log.txt',
                transfer_type: 'log.txt',
            },
        });
    }
    set_initial_email(email) {
        return this.call_v2({
            method: 'set_initial_email',
            data: {
                email,
            },
        });
    }
    resend_email_confirmation() {
        return this.call_v2({
            method: 'resend_email_confirmation',
        });
    }
    mark_events_as_read() {
        return this.call_v2({
            method: 'mark_events_as_read',
        });
    }
    cancel_filetransfers(device_id, options) {
        return this.call_v2({
            method: 'cancel_filetransfers',
            data: {
                device_id,
                ...options,
            },
        });
    }
    load_tel_password(device_id) {
        return this.call_v2({
            method: 'get_tel_password',
            data: { device_id },
        });
    }
    set_new_ui() {
        return this.call_v2({
            method: 'set_new_ui',
        });
    }
    load_z3k_device_config_history(device_id, config_numbers = 40) {
        return this.call_v2({
            method: 'get_z3k_config_history',
            data: { device_id, config_numbers },
        });
    }
    set_z3k_device_config_from_history(device_id, config_id) {
        return this.call_v2({
            method: 'set_z3k_config_from_history',
            data: { device_id, config_id },
        });
    }
    change_save_for_z3k_config_history(device_id, config_id, flag) {
        return this.call_v2({
            method: 'change_save_for_z3k_config_in_history',
            data: { device_id, config_id, flag },
        });
    }
    get_baseurl() {
        return this.options?.baseurl ?? window.ZONT_URL.api;
    }
    download_archive_to_cloud(device_id, min_time, max_time, split_by) {
        return this.call_v2({
            method: 'download_archive_to_cloud',
            data: {
                device_id: device_id,
                mintime: min_time,
                maxtime: max_time,
                split_by: split_by,
            },
        });
    }
    delete_archive_cron_task(task_id) {
        return this.call_v2({
            method: 'delete_cron_task',
            data: {
                task_id: task_id,
            },
        });
    }
    add_archive_task(device_id, period) {
        return this.call_v2({
            method: 'add_cron_task',
            data: {
                task_type: 'archiver_task',
                device_id: device_id,
                period: period,
                timezone: 3,
            },
        });
    }
    get_task_list() {
        return this.call_v2({
            method: 'get_list_tasks',
            data: {},
        });
    }
    get_tokens_info() {
        return this.call_v2({
            method: 'get_alien_tokens',
            data: {},
        });
    }
    delete_alien_token(token_id) {
        return this.call_v2({
            method: 'delete_alien_token',
            data: { token_id: token_id },
        });
    }
    get_alien_app_urls() {
        return this.call_v2({
            method: 'get_alien_app_urls',
            data: {},
        });
    }
    get_ui_guide_groups() {
        return this.call_v2({
            method: 'get_ui_guide_groups',
            data: {},
        });
    }
    change_ui_stories(data) {
        return this.call_v2({
            method: 'change_ui_stories',
            data: { id: data },
        });
    }
    set_status_for_guide(data) {
        return this.call_v2({
            method: 'change_ui_guide_group',
            data,
        });
    }
    request_devices(device_ids) {
        return this.call_v2({
            method: 'devices',
            data: { load_io: true, device_ids },
        });
    }
    show_recount(target_tariff, sim_id, timezone) {
        return this.call_v2({
            method: 'show_recount',
            data: {
                target_tariff,
                sim_id,
                timezone,
            },
        });
    }
    request_get_all_states(device_id) {
        return this.call_v2({
            method: 'request_z3k_states',
            data: { device_id },
        });
    }
    get_billing_history() {
        return this.call_v2({
            method: 'get_billing_history',
        });
    }
    clear_ecu_errors(data) {
        return this.call_v2({
            method: 'clear_ecu_errors',
            data,
        });
    }
    reset_ztc_thermometers(data) {
        return this.call_v2({
            method: 'reset_ztc_thermometers',
            data,
        });
    }
    reset_thermometers(data) {
        return this.call_v2({
            method: 'reset_thermometers',
            data,
        });
    }
    wake_camera(data) {
        return this.call_v2({
            method: 'wake_camera',
            data,
        });
    }
    request_opentherm_diag(data) {
        return this.call_v2({
            method: 'request_opentherm_diag',
            data,
        });
    }
    request_opentherm_count(data) {
        return this.call_v2({
            method: 'request_opentherm_count',
            data,
        });
    }
    rf_load_devices(data) {
        return this.call_v2({
            method: 'rf_load_devices',
            data,
        });
    }
    rf_delete_device(data) {
        return this.call_v2({
            method: 'rf_delete_device',
            data,
        });
    }
    rf_delete_all(data) {
        return this.call_v2({
            method: 'rf_delete_all',
            data,
        });
    }
    rf_add_devices(data) {
        return this.call_v2({
            method: 'rf_add_devices',
            data,
        });
    }
    get_device_spec() {
        return this.call_v2({
            method: 'get_device_spec',
        });
    }
    async call_v2(callOptions) {
        const url = this.get_baseurl() + callOptions.method;
        const params = this.prepareRequestParams(callOptions);
        let response, json;
        try {
            response = await fetch(url, params);
            json = await response.json();
        }
        catch (error) {
            throw { json: null, response, error };
        }
        if (json.ok) {
            return json;
        }
        throw { json, response };
    }
    async call_to_get_blob(callOptions) {
        const url = this.get_baseurl() + callOptions.method;
        const params = this.prepareRequestParams(callOptions);
        let response, blob;
        try {
            response = await fetch(url, params);
            blob = await response.blob();
        }
        catch (error) {
            throw { blob: null, response, error };
        }
        return blob;
    }
    async call(url, callOptions) {
        const _url = this.get_baseurl() + url;
        const params = this.prepareRequestParams(callOptions);
        let response, json;
        try {
            response = await fetch(_url, params);
            json = await response.json();
        }
        catch (error) {
            throw { json: null, response, error };
        }
        if (json.ok) {
            return json;
        }
        throw { json, response };
    }
}
export * from './api-context';
export * from './api-types';
